import { ChatModule } from '../chat/chat.module';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgxColorsModule } from 'ngx-colors';
import { SharedModule } from '../shared/shared.module';
import { AutoBillingSettingsComponent } from './components/billing-settings/auto-billing-settings/auto-billing-settings.component';
import { BillingSettingsComponent } from './components/billing-settings/billing-settings.component';
import { CodingBundleSettingsComponent } from './components/billing-settings/coding-bundle-settings/coding-bundle-settings.component';
import { ColleagueSettingsComponent } from './components/colleague-settings/colleague-settings.component';
import { ConsentFormSettingsComponent } from './components/consent-form-settings/consent-form-settings.component';
import { InformationSheetUploadComponent } from './components/consent-form-settings/information-sheet-upload/information-sheet-upload.component';
import { NoteTypeSettingsComponent } from './components/note-type-settings/note-type-settings.component';
import { ChangePasswordDialogComponent } from './components/profile-settings/change-password-dialog/change-password-dialog.component';
import { EditProfileDialogComponent } from './components/profile-settings/edit-profile-dialog/edit-profile-dialog.component';
import { ProfileSettingsComponent } from './components/profile-settings/profile-settings.component';
import { ScheduleSettingsComponent } from './components/schedule-settings/schedule-settings.component';
import { SettingsContainerComponent } from './components/settings-container/settings-container.component';
import { SubscriptionSettingsComponent } from './components/subscription-settings/subscription-settings.component';
import { ConnectPanelComponent } from './components/subscription-settings/connect-panel/connect-panel.component';
import { TokenUsageComponent } from './components/token-usage/token-usage.component';
import { WardSettingsComponent } from './components/ward-settings/ward-settings.component';
import { TextMessageUsageComponent } from './components/text-message-usage/text-message-usage.component';
import { AccountingSettingsComponent } from './components/accounting-settings/accounting-settings.component';
import { SurveyTemplateSettingsComponent } from './components/survey-template-settings/survey-template-settings.component';
import { CustomFormSettingsComponent } from './components/custom-form-settings/custom-form-settings.component';
import { CustomFormBuilderComponent } from './components/custom-form-settings/custom-form-builder/custom-form-builder.component';
import { CustomFormImportDialogComponent } from './components/custom-form-settings/custom-form-import/custom-form-import-dialog.component';
import { PracticeDocumentSettingsComponent } from './components/practice-document-settings/practice-document-settings.component';
import { ReferralNetworkSettingsComponent } from './components/referral-network-settings/referral-network-settings.component';
import { ReferralInviteDialogComponent } from './components/referral-network-settings/referral-invite-dialog/referral-invite-dialog.component';
import { ReferralPracticeLinkDialogComponent } from './components/referral-network-settings/referral-practice-link-dialog/referral-practice-link-dialog.component';
import { PracticeDocumentUploadDialogComponent } from './components/practice-document-settings/practice-document-upload-dialog/practice-document-upload-dialog.component';
import { PracticeDocumentEditDialogComponent } from './components/practice-document-settings/practice-document-edit-dialog/practice-document-edit-dialog.component';
import { PracticeDocumentTriggerDialogComponent } from './components/practice-document-settings/practice-document-trigger-dialog/practice-document-trigger-dialog.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDividerModule } from '@angular/material/divider';

const MAT_MODULES = [
  MatCardModule,
  MatFormFieldModule,
  MatInputModule,
  MatButtonModule,
  MatIconModule,
  MatSelectModule,
  MatListModule,
  MatDialogModule,
  MatTabsModule,
  MatChipsModule,
  MatCheckboxModule,
  MatRadioModule,
  MatProgressBarModule,
  MatProgressSpinnerModule,
  MatExpansionModule,
  MatTableModule,
  MatDividerModule
]

@NgModule({
  declarations: [
    SettingsContainerComponent,
    ProfileSettingsComponent,
    ScheduleSettingsComponent,
    NoteTypeSettingsComponent,
    WardSettingsComponent,
    ConsentFormSettingsComponent,
    SubscriptionSettingsComponent,
    ConnectPanelComponent,
    EditProfileDialogComponent,
    ChangePasswordDialogComponent,
    ColleagueSettingsComponent,
    BillingSettingsComponent,
    AutoBillingSettingsComponent,
    CodingBundleSettingsComponent,
    InformationSheetUploadComponent,
    TokenUsageComponent,
    TextMessageUsageComponent,
    AccountingSettingsComponent,
    SurveyTemplateSettingsComponent,
    // Practice forms (tasks/patient-forms.md §5.1/§5.2). The builder's live preview is
    // SharedModule's DynamicFormRendererComponent, already imported via SharedModule below.
    CustomFormSettingsComponent,
    CustomFormBuilderComponent,
    // §16 — the PDF import dialog. Opened by the settings tab through MatDialog; it produces a
    // DRAFT schema for the builder and can publish nothing.
    CustomFormImportDialogComponent,
    // Practice documents (tasks/practice-documents.md §1.4/§7 phase 1) — the Documents tab. A
    // SEPARATE feature from the practice forms above: that one is a builder and is hidden, this one
    // uploads the PDFs a practice already has and ships visible.
    PracticeDocumentSettingsComponent,
    ReferralNetworkSettingsComponent,
    ReferralInviteDialogComponent,
    ReferralPracticeLinkDialogComponent,
    // Phase 8 — the tab's two dialogs, opened imperatively through MatDialog and needing no export.
    // TWO rather than one: the upload is a multipart create whose accepted body is a name, a
    // category and a description; the edit is a JSON PUT of ten delivery fields plus, separately, a
    // re-inspecting POST /:id/file. One component would have been a mode flag hiding most of its
    // own controls.
    PracticeDocumentUploadDialogComponent,
    PracticeDocumentEditDialogComponent,
    PracticeDocumentTriggerDialogComponent,
  ],
  imports: [
    CommonModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgxColorsModule,
    NgxChartsModule,
    DragDropModule,
    ChatModule,
  ],
  exports: [
    SettingsContainerComponent,
  ]
})
export class GroupModule { }
